(function () {
  'use strict';

  /**
   * Cart drawer add-ons: free-shipping progress bar + upsells + promo refresh.
   *
   * IMPORTANT — why this is event-driven and NOT observer-driven:
   * The Motion (Archetype) theme already updates the cart drawer itself when a
   * line quantity changes. It POSTs to /cart/change.js, then rebuilds the line
   * items via /cart?view=ajax and re-binds its own +/- handlers (theme.QtySelector).
   * Our only job is to refresh the bits Motion doesn't manage: the shipping bar
   * and the upsell list.
   *
   * We do that by listening to the events Motion dispatches on `document`
   * (cart:updated, cart:build, ajaxProduct:added). We deliberately do NOT:
   *   - observe the drawer DOM with MutationObserver, or
   *   - monkey-patch window.fetch / jQuery.ajax
   * because our own refresh writes back into the drawer (shipping bar + upsell
   * markup), which re-triggered those observers and created a perpetual
   * refresh -> fetch -> mutate -> refresh loop. That loop saturated the main
   * thread and the network and made the native +/- update intermittently
   * freeze (its requests got queued behind the storm, and the qty row was left
   * with `is-loading` => pointer-events:none).
   */

  var CartCustom = {
    state: {
      inFlight: false,
      rerun: false
    },

    init: function () {
      var drawer = document.getElementById('CartDrawer');
      if (!drawer) return;

      this.drawer = drawer;
      this.shippingBar = drawer.querySelector('[data-shipping-bar]');
      this.upsellsContainer = drawer.querySelector('[data-cart-upsells]');

      var self = this;

      // Single debounced entry point shared by every trigger, so a burst of
      // events (e.g. change + rebuild) coalesces into one refresh.
      this.refreshSoon = this.debounce(function () { self.refresh(); }, 150);

      // Motion dispatches these on `document`. A couple of integrations use
      // documentElement, so we listen on both with the same handler.
      var events = ['cart:updated', 'cart:build', 'ajaxProduct:added', 'cart:refresh'];
      events.forEach(function (evtName) {
        document.addEventListener(evtName, self.refreshSoon);
        document.documentElement.addEventListener(evtName, self.refreshSoon);
      });

      // Upsell "add" buttons live inside the drawer (delegated click).
      drawer.addEventListener('click', this.handleClick.bind(this));

      // Initial hydration.
      this.refresh();
    },

    /**
     * Fetch the authoritative cart once and update shipping bar + upsells.
     * Re-entrancy safe: if asked again while a refresh is in flight, we run
     * exactly one more refresh when the current one settles, so we always end
     * on fresh data without looping.
     */
    refresh: function () {
      var self = this;

      if (this.state.inFlight) {
        this.state.rerun = true;
        return;
      }
      this.state.inFlight = true;

      fetch('/cart.js', { credentials: 'same-origin', headers: { 'Cache-Control': 'no-cache' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          // Re-grab references in case Motion rebuilt part of the drawer.
          self.shippingBar = self.drawer.querySelector('[data-shipping-bar]');
          self.upsellsContainer = self.drawer.querySelector('[data-cart-upsells]');
          self.updateShippingBar(cart);
          self.updateUpsells(cart);
          self.clearStuckQty();
        })
        .catch(function (err) { console.warn('[CartCustom] refresh failed', err); })
        .finally(function () {
          self.state.inFlight = false;
          if (self.state.rerun) {
            self.state.rerun = false;
            self.refresh();
          }
        });
    },

    /**
     * Safety net. If a line's quantity wrapper was left disabled
     * (.is-loading => opacity:.5; pointer-events:none) because a rebuild didn't
     * complete, re-enable it so the buttons never stay frozen.
     */
    clearStuckQty: function () {
      var stuck = this.drawer.querySelectorAll('.js-qty__wrapper.is-loading');
      for (var i = 0; i < stuck.length; i++) {
        stuck[i].classList.remove('is-loading');
      }
    },

    updateShippingBar: function (cart) {
      var bar = this.shippingBar;
      if (!bar) return;

      var threshold = parseInt(bar.dataset.threshold, 10);
      if (!threshold || threshold <= 0) return;

      var total = cart.total_price;
      var remaining = Math.max(0, threshold - total);
      var progress = Math.min(100, (total / threshold) * 100);

      var textEl = bar.querySelector('[data-shipping-bar-text]');
      var fillEl = bar.querySelector('[data-shipping-bar-fill]');

      if (fillEl) {
        fillEl.style.width = progress + '%';
      }

      if (textEl) {
        if (remaining <= 0) {
          textEl.innerHTML = '<span class="cart__shipping-bar-icon">🎉</span> Livraison offerte débloquée';
          bar.classList.add('is-unlocked');
        } else {
          textEl.innerHTML = 'Plus que <strong>' + this.formatMoney(remaining) + '</strong> pour la livraison offerte';
          bar.classList.remove('is-unlocked');
        }
      }

      bar.style.display = cart.item_count === 0 ? 'none' : '';
    },

    updateUpsells: function (cart) {
      var container = this.upsellsContainer;
      if (!container) return;

      if (cart.item_count === 0) {
        container.hidden = true;
        return;
      }

      var source = container.dataset.source;
      var self = this;

      if (source === 'recommendations') {
        this.fetchRecommendations(cart);
        return;
      }

      fetch('/?sections=cart-upsells', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var html = data['cart-upsells'];
          self.renderUpsellsHTML(html);

          if (source === 'metafield') {
            var fallback = container.querySelector('[data-upsells-fallback]');
            if (fallback) {
              self.fetchRecommendations(cart);
            }
          }
        })
        .catch(function (err) { console.warn('[CartCustom] upsell fetch failed', err); });
    },

    fetchRecommendations: function (cart) {
      if (!cart.items || !cart.items.length) return;
      var productId = cart.items[0].product_id;
      var url = '/recommendations/products?product_id=' + productId + '&limit=4&intent=complementary&section_id=cart-upsells';

      var self = this;
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (html) { self.renderUpsellsHTML(html); })
        .catch(function (err) { console.warn('[CartCustom] recommendations fetch failed', err); });
    },

    renderUpsellsHTML: function (html) {
      var container = this.upsellsContainer;
      if (!container) return;

      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var inner = tmp.querySelector('[data-upsells-inner]');
      var listEl = container.querySelector('[data-upsells-list]');

      if (!inner || !listEl) {
        container.hidden = true;
        return;
      }

      var cards = inner.querySelectorAll('[data-upsell-card]');
      if (cards.length === 0) {
        container.hidden = true;
        return;
      }

      listEl.innerHTML = '';
      for (var i = 0; i < cards.length; i++) {
        listEl.appendChild(cards[i]);
      }
      container.hidden = false;
    },

    handleClick: function (e) {
      var btn = e.target.closest('[data-upsell-add]');
      if (!btn || btn.disabled) return;

      e.preventDefault();
      var variantId = btn.dataset.variantId;
      if (!variantId) return;

      var originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = '...';

      var self = this;
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed: ' + r.status);
          return r.json();
        })
        .then(function () {
          // Hand the rebuild back to Motion. Its CartForm listens for
          // `cart:build` on `document`, rebuilds the line items, and crucially
          // RE-BINDS theme.QtySelector on the new rows. (The previous version
          // overwrote [data-products] innerHTML by hand, which left the new
          // +/- buttons with no event listeners => frozen after an upsell add.)
          document.dispatchEvent(new CustomEvent('cart:build'));
          // Refresh shipping bar + upsells from the new cart.
          self.refreshSoon();
        })
        .catch(function (err) {
          console.warn('[CartCustom] upsell add failed', err);
        })
        .finally(function () {
          btn.disabled = false;
          btn.textContent = originalText;
        });
    },

    formatMoney: function (cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }
      var cur = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) ? Shopify.currency.active : 'EUR';
      var syms = { EUR: '\u20AC', GBP: '\u00A3', USD: '$', CAD: '$', AUD: '$', CHF: 'CHF' };
      var sym = syms[cur] || cur;
      var suffix = (cur === 'EUR' || cur === 'CHF');
      var value = (cents / 100).toFixed(2).replace('.', cur === 'EUR' ? ',' : '.');
      return suffix ? value + (cur === 'EUR' ? '' : ' ') + sym : sym + value;
    },

    debounce: function (fn, wait) {
      var t;
      return function () {
        var ctx = this;
        var args = arguments;
        clearTimeout(t);
        t = setTimeout(function () { fn.apply(ctx, args); }, wait);
      };
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { CartCustom.init(); });
  } else {
    CartCustom.init();
  }
})();